import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';

export interface ComparisonPreferencesState {
  salaryWeight: number;
  vacationWeight: number;
  benefitsWeight: number;
  remotenessWeight: number;
  languagesWeight: number;
  titleWeight: number;
  roleLevelWeight: number;
  locationWeight: number;
  jobTypeWeight: number;
}

const initialState: ComparisonPreferencesState = {
  salaryWeight: 0.15,
  vacationWeight: 0.1,
  benefitsWeight: 0.1,
  remotenessWeight: 0.15,
  languagesWeight: 0.1,
  titleWeight: 0.2,
  roleLevelWeight: 0.1,
  locationWeight: 0.1,
  jobTypeWeight: 0.05,
};

/** Normalize rehydrated state that may lack languagesWeight (old 4-weight schema) or the 4 newest weights (old 5-weight schema). */
function migrateWeights(state: Partial<ComparisonPreferencesState>): ComparisonPreferencesState {
  const hasNewestWeights =
    typeof state.titleWeight === 'number' &&
    typeof state.roleLevelWeight === 'number' &&
    typeof state.locationWeight === 'number' &&
    typeof state.jobTypeWeight === 'number';

  if (hasNewestWeights) {
    return {
      salaryWeight: state.salaryWeight ?? initialState.salaryWeight,
      vacationWeight: state.vacationWeight ?? initialState.vacationWeight,
      benefitsWeight: state.benefitsWeight ?? initialState.benefitsWeight,
      remotenessWeight: state.remotenessWeight ?? initialState.remotenessWeight,
      languagesWeight: state.languagesWeight ?? initialState.languagesWeight,
      titleWeight: state.titleWeight as number,
      roleLevelWeight: state.roleLevelWeight as number,
      locationWeight: state.locationWeight as number,
      jobTypeWeight: state.jobTypeWeight as number,
    };
  }

  if (state.languagesWeight != null && typeof state.languagesWeight === 'number') {
    // Old 5-weight schema (salary/vacation/benefits/remoteness/languages summing to ~1.0).
    // Rescale it down to 0.6 and split the remaining 0.4 across the 4 new weights.
    const sum =
      (state.salaryWeight ?? 0) +
      (state.vacationWeight ?? 0) +
      (state.benefitsWeight ?? 0) +
      (state.remotenessWeight ?? 0) +
      (state.languagesWeight ?? 0);
    const scale = sum > 0 ? 0.6 / sum : 0.12;
    return {
      salaryWeight: (state.salaryWeight ?? 0.2) * scale,
      vacationWeight: (state.vacationWeight ?? 0.2) * scale,
      benefitsWeight: (state.benefitsWeight ?? 0.2) * scale,
      remotenessWeight: (state.remotenessWeight ?? 0.2) * scale,
      languagesWeight: state.languagesWeight * scale,
      titleWeight: 0.2,
      roleLevelWeight: 0.1,
      locationWeight: 0.1,
      jobTypeWeight: 0,
    };
  }

  // Old 4-weight schema (salary/vacation/benefits/remoteness summing to ~0.8, no languages).
  const sum = (state.salaryWeight ?? 0) + (state.vacationWeight ?? 0) + (state.benefitsWeight ?? 0) + (state.remotenessWeight ?? 0);
  const scale = sum > 0 ? 0.48 / sum : 0.12;
  return {
    salaryWeight: (state.salaryWeight ?? 0.2) * scale,
    vacationWeight: (state.vacationWeight ?? 0.2) * scale,
    benefitsWeight: (state.benefitsWeight ?? 0.2) * scale,
    remotenessWeight: (state.remotenessWeight ?? 0.2) * scale,
    languagesWeight: 0.12,
    titleWeight: 0.2,
    roleLevelWeight: 0.1,
    locationWeight: 0.1,
    jobTypeWeight: 0,
  };
}

const comparisonPreferencesSlice = createSlice({
  name: 'comparisonPreferences',
  initialState,
  reducers: {
    setSalaryWeight: (state, action: PayloadAction<number>) => {
      state.salaryWeight = action.payload;
    },
    setVacationWeight: (state, action: PayloadAction<number>) => {
      state.vacationWeight = action.payload;
    },
    setBenefitsWeight: (state, action: PayloadAction<number>) => {
      state.benefitsWeight = action.payload;
    },
    setRemotenessWeight: (state, action: PayloadAction<number>) => {
      state.remotenessWeight = action.payload;
    },
    setLanguagesWeight: (state, action: PayloadAction<number>) => {
      state.languagesWeight = action.payload;
    },
    setTitleWeight: (state, action: PayloadAction<number>) => {
      state.titleWeight = action.payload;
    },
    setRoleLevelWeight: (state, action: PayloadAction<number>) => {
      state.roleLevelWeight = action.payload;
    },
    setLocationWeight: (state, action: PayloadAction<number>) => {
      state.locationWeight = action.payload;
    },
    setJobTypeWeight: (state, action: PayloadAction<number>) => {
      state.jobTypeWeight = action.payload;
    },
    setAllWeights: (state, action: PayloadAction<ComparisonPreferencesState>) => {
      state.salaryWeight = action.payload.salaryWeight;
      state.vacationWeight = action.payload.vacationWeight;
      state.benefitsWeight = action.payload.benefitsWeight;
      state.remotenessWeight = action.payload.remotenessWeight;
      state.languagesWeight = action.payload.languagesWeight;
      state.titleWeight = action.payload.titleWeight;
      state.roleLevelWeight = action.payload.roleLevelWeight;
      state.locationWeight = action.payload.locationWeight;
      state.jobTypeWeight = action.payload.jobTypeWeight;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(REHYDRATE, (state, action) => {
      const payload = (action as any).payload;
      const persisted = payload?.comparisonPreferences;
      if (persisted && typeof persisted === 'object') {
        const migrated = migrateWeights(persisted);
        state.salaryWeight = migrated.salaryWeight;
        state.vacationWeight = migrated.vacationWeight;
        state.benefitsWeight = migrated.benefitsWeight;
        state.remotenessWeight = migrated.remotenessWeight;
        state.languagesWeight = migrated.languagesWeight;
        state.titleWeight = migrated.titleWeight;
        state.roleLevelWeight = migrated.roleLevelWeight;
        state.locationWeight = migrated.locationWeight;
        state.jobTypeWeight = migrated.jobTypeWeight;
      }
    });
  },
});

export const {
  setSalaryWeight,
  setVacationWeight,
  setBenefitsWeight,
  setRemotenessWeight,
  setLanguagesWeight,
  setTitleWeight,
  setRoleLevelWeight,
  setLocationWeight,
  setJobTypeWeight,
  setAllWeights,
} = comparisonPreferencesSlice.actions;

export default comparisonPreferencesSlice.reducer;
